import {ReactElement, useContext} from 'react';
import {ListboxItemProps} from '@common/ui/forms/listbox/item';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useLogout} from '@common/auth/requests/logout';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useSettings} from '@common/core/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {
  Menu,
  MenuItem,
  MenuTrigger,
} from '@common/ui/navigation/menu/menu-trigger';
import {NotificationsIcon} from '@common/icons/material/Notifications';
import {Trans} from '@common/i18n/trans';
import {PaymentsIcon} from '@common/icons/material/Payments';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {AccountCircleIcon} from '@common/icons/material/AccountCircle';
import {DarkModeIcon} from '@common/icons/material/DarkMode';
import {LightModeIcon} from '@common/icons/material/LightMode';
import {ExitToAppIcon} from '@common/icons/material/ExitToApp';
import {StarIcon} from '@common/icons/material/Star';
import {
  useGetProfiles,
  useSelectProfile,
} from '@app/profile/requests/use-get-profiles';
import {Spinner} from 'react-bootstrap';
import {Add} from 'iconsax-react';
import {openDialog} from '@common/ui/overlays/store/dialog-store';
import AddProfileDialog from '@app/addProfileDialog';

interface Props {
  children: ReactElement;
  items?: ReactElement<ListboxItemProps>[];
}
export function NavbarAuthMenu({children, items}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {notifications, themes} = useSettings();
  const {user, isSubscribed} = useAuth();
  const navigate = useNavigate();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const getProfiles = useGetProfiles();
  const selectProfile = useSelectProfile();

  if (!selectedTheme || !user) return null;
  const hasUnreadNotif = !!user.unread_notifications_count;

  const notifMenuItem = (
    <MenuItem
      className="md:hidden"
      value="notifications"
      startIcon={<NotificationsIcon />}
      onSelected={() => {
        navigate('/notifications');
      }}
    >
      <Trans message="Notifications" />
      {hasUnreadNotif ? ` (${user.unread_notifications_count})` : undefined}
    </MenuItem>
  );

  const billingMenuItem = (
    <MenuItem
      value="billing"
      startIcon={<PaymentsIcon />}
      onSelected={() => {
        navigate('/billing');
      }}
    >
      <Trans message="Billing" />
    </MenuItem>
  );

  return (
    <MenuTrigger>
      {children}
      <Menu>
        {menu &&
          menu.items.map(item => {
            const Icon = item.icon && createSvgIconFromTree(item.icon);
            return (
              <MenuItem
                value={item.id}
                key={item.id}
                startIcon={Icon && <Icon />}
                onSelected={() => {
                  if (item.type === 'link') {
                    window.open(item.action, '_blank');
                  } else {
                    navigate(item.action);
                  }
                }}
                onClick={() => {
                  if (
                    item.label === 'Admin area' ||
                    item.label === 'Admin Area' ||
                    item.label === 'admin area'
                  ) {
                    window.location.reload();
                  }
                }}
              >
                <Trans message={item.label} />
              </MenuItem>
            );
          })}
        {auth.getUserProfileLink && (
          <MenuItem
            value="profile"
            startIcon={<AccountCircleIcon />}
            onSelected={() => {
              navigate(auth.getUserProfileLink!(user));
            }}
          >
            <Trans message="Profile page" />
          </MenuItem>
        )}
        {items?.map(item => item)}
        {notifications?.integrated ? notifMenuItem : undefined}
        {/* {isSubscribed && billingMenuItem} */}
        {themes?.user_change && !selectedTheme.is_dark && (
          <MenuItem
            value="light"
            startIcon={<DarkModeIcon />}
            onSelected={() => {
              selectTheme('dark');
            }}
          >
            <Trans message="Dark mode" />
          </MenuItem>
        )}
        {themes?.user_change && selectedTheme.is_dark && (
          <MenuItem
            value="dark"
            startIcon={<LightModeIcon />}
            onSelected={() => {
              selectTheme('light');
            }}
          >
            <Trans message="Light mode" />
          </MenuItem>
        )}
        {getProfiles?.data?.length !== 0 && (
          <div className="px-20 py-8">
            <span>
              <Trans message="Profiles" />
            </span>
            {getProfiles && getProfiles?.data && getProfiles?.data?.map(item => (
              <div
                className="flex cursor-pointer gap-10 px-20 py-8 hover:bg-hover"
                onClick={() => selectProfile.mutate(item.id)}
              >
                {item?.avatar?.url ? (
                  <img
                    className="h-[24px] w-[24px] rounded-full"
                    src={item?.avatar?.url}
                    alt=""
                  />
                ) : (
                  <AccountCircleIcon />
                )}
                <span
                  className={`text-base ${user?.profiles && user?.profiles[0]?.id === item.id ? 'text-danger-darker' : 'text-white'}`}
                >
                  {item.name}
                </span>
              </div>
            ))}{' '}
            {selectProfile.isPending ? <Spinner size="sm" /> : null}
            <div
              className="flex cursor-pointer gap-10 px-20 py-8 hover:bg-hover"
              onClick={() => openDialog(AddProfileDialog)}
            >
              <Add />
              <span className={`text-base text-white`}>
                <Trans message="Add New" />
              </span>
            </div>
          </div>
        )}

		 <MenuItem
          value="manage-points"
          startIcon={<StarIcon />}
          onSelected={() => {
            navigate(`/user/${user.id}/user/invite-friends`);
          }}
        >
          <Trans message="Points Management" />
        </MenuItem>

        <MenuItem
          value="logout"
          startIcon={<ExitToAppIcon />}
          onSelected={() => {
            logout.mutate();
          }}
        >
          <Trans message="Log out" />
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}
